import { Award, DollarSign, MapPinned, FileCheck2, Gem, Users } from "lucide-react";
import useCountUp from "@/lib/useCountUp";

const STATS = [
  {
    icon: Award,
    countTo: 19,
    suffix: "+",
    unit: "Years",
    label: "Industry Experience",
    desc: "Across public adjusting, appraisal & subrogation.",
    featured: false,
  },
  {
    icon: DollarSign,
    countTo: 500,
    prefix: "$",
    suffix: "M+",
    label: "Claims Recovery Experience",
    desc: "Complex property losses and high-value disputes.",
    featured: true,
  },
  {
    icon: MapPinned,
    static: "FL & GA",
    label: "Regional Coverage",
    desc: "Florida and Georgia jurisdictions with national vision.",
    featured: false,
  },
  {
    icon: FileCheck2,
    countTo: 100,
    suffix: "%",
    label: "Litigation-Ready Documentation",
    desc: "Every report built to withstand scrutiny.",
    featured: false,
  },
  {
    icon: Gem,
    countTo: 1.5,
    prefix: "$",
    suffix: "M+",
    decimals: 1,
    label: "Luxury Properties",
    desc: "Specialized reporting for high-value homeowners.",
    featured: true,
  },
  {
    icon: Users,
    static: "HOA & Commercial",
    label: "Stakeholder Expertise",
    desc: "Board-level risk strategy and oversight.",
    featured: false,
  },
];

const StatCard = ({ s, i }) => {
  const Icon = s.icon;
  const { ref, value } = useCountUp({
    end: s.countTo ?? 0,
    duration: 2000,
    decimals: s.decimals ?? 0,
  });
  const displayed = s.static
    ? s.static
    : `${s.prefix ?? ""}${s.decimals ? value.toFixed(s.decimals) : Math.round(value)}${s.suffix ?? ""}`;

  return (
    <div
      ref={ref}
      className={`btr-reveal relative rounded-lg p-7 transition-all btr-glass ${
        s.featured ? "btr-stat-featured border-[#a2020b]/40" : "btr-glass-red"
      }`}
      style={{ transitionDelay: `${i * 60}ms` }}
      data-testid={`stat-card-${i}`}
    >
      {/* Red vertical accent */}
      <div className="absolute top-6 left-0 w-[2px] h-10 bg-gradient-to-b from-[#a2020b] via-[#a2020b] to-transparent rounded-r" />
      {/* Ambient highlight */}
      <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-[#a2020b]/12 blur-3xl pointer-events-none" />

      <div className="flex items-center justify-between mb-6">
        <div className="w-10 h-10 rounded-md bg-[#a2020b]/12 border border-[#a2020b]/35 flex items-center justify-center">
          <Icon className="w-4.5 h-4.5 text-[#a2020b]" strokeWidth={1.5} />
        </div>
        {s.featured && (
          <span className="text-[9px] font-display tracking-[0.25em] uppercase text-[#a2020b]">
            Featured
          </span>
        )}
      </div>

      <div className="font-display btr-num font-extrabold text-3xl sm:text-4xl tracking-tight">
        {displayed}
        {s.unit && <span className="text-zinc-500 text-xl ml-2">{s.unit}</span>}
      </div>
      <div className="font-display text-white/85 text-sm font-semibold uppercase tracking-[0.12em] mt-3">
        {s.label}
      </div>
      <p className="font-body text-zinc-400 text-sm mt-3 leading-relaxed">{s.desc}</p>
    </div>
  );
};

export const TrackRecord = () => {
  return (
    <section id="about" className="relative py-20 sm:py-24 md:py-32 btr-bg-charcoal overflow-hidden" data-testid="track-record">
      <div className="btr-beam" style={{ width: 520, height: 520, top: "-10%", right: "-10%", background: "#a2020b", opacity: 0.2 }} />
      <div className="absolute inset-0 btr-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="btr-reveal max-w-3xl">
          <span className="btr-badge">
            <span className="dot" />
            Proven Track Record
          </span>
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-6xl mt-6 leading-[1.05] sm:leading-[1.02] tracking-tight">
            A Proven Record in <span className="text-[#a2020b]">High-Stakes</span> Protection.
          </h2>
          <p className="font-body text-zinc-300/85 text-base sm:text-lg mt-6 leading-relaxed">
            Before The Risk was built from nearly two decades of experience across public adjusting, appraisal,
            subrogation, insurance sales, and complex property loss environments.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
          {STATS.map((s, i) => (
            <StatCard key={s.label} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
